import React from "react";
import { ShoppingCart, AlertTriangle, CheckCircle } from "lucide-react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  CardText,
  Badge,
} from "reactstrap";
import {
  extractTierFromName,
  getProductTierBadgeColor,
} from "../../../../../utility/inventoryUtils";
import "./index.css";
import { ProductSummary } from "../../../../../hooks/useFirebaseProductSummaries";

interface CartChannelTabProps {
  productSummaries: ProductSummary[];
}

export const CartChannelTab: React.FC<CartChannelTabProps> = ({
  productSummaries,
}) => {

  if (!productSummaries || productSummaries.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="alert alert-warning">
          <h5>No product data available</h5>
          <p className="mb-0">
            No product summaries data found. Please check if data exists in
            Firebase.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="inventory-cart-channel__tab-content">
      {productSummaries.map((product) => {
        const tier = extractTierFromName(product.displayName);
        const totalAllocated = Object.values(product.channels || {}).reduce(
          (sum, channel) => sum + (channel.allocated || 0),
          0
        );
        const totalOrders = Object.values(product.channels || {}).reduce(
          (sum, channel) => sum + (channel.reserved || 0),
          0
        );

        return (
          <Card
            key={product.id}
            className="inventory-cart-channel__product-card mb-4"
          >
            <CardHeader className="inventory-cart-channel__product-card-header">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <CardTitle className="inventory-cart-channel__product-name h5 mb-1">
                    {product.displayName}
                  </CardTitle>
                  <CardText className="inventory-cart-channel__product-sku text-muted small mb-0">
                    <span className="me-3">
                      {product.productId || "No Product ID"}
                    </span>
                    <span className="me-3">Allocated: {totalAllocated}</span>
                    <span>Orders: {totalOrders}</span>
                  </CardText>
                </div>
                <div className="d-flex align-items-center">
                  <Badge
                    color={getProductTierBadgeColor(tier)}
                    className="inventory-cart-channel__product-tier-badge me-3"
                  >
                    {tier}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardBody className="inventory-cart-channel__product-card-body">
              <div className="inventory-cart-channel__cart-list">
                {Object.entries(product.carts || {}).map(([cartId, cart]) => {
                  // Calculate cart totals from cart.channels
                  const cartAllocated = Object.values(cart.channels || {}).reduce(
                    (sum, channel) => sum + (channel.allocated || 0),
                    0
                  );
                  
                  return (
                    <div
                      key={cartId}
                      className="inventory-cart-channel__cart-item"
                    >
                      <div className="inventory-cart-channel__cart-header d-flex align-items-center mb-4">
                        <div className="inventory-cart-channel__cart-icon-container me-3">
                          <ShoppingCart className="inventory-cart-channel__cart-icon" />
                        </div>
                        <div className="flex-grow-1">
                          <span className="inventory-cart-channel__cart-id d-block">
                            {cart.cartName}
                          </span>
                        </div>
                        <div className="text-end">
                          <Badge
                            color="light"
                            className="inventory-cart-channel__cart-total-badge"
                          >
                              <small className="inventory-cart-channel__text--muted">
                              Allocated
                            </small>
                            <div className="h4 mb-0 inventory-cart-channel__text--primary">
                              {cartAllocated}
                            </div>
                          
                          </Badge>
                        </div>
                      </div>

                      <h6 className="mb-3 inventory-cart-channel__text--muted">
                        Channel-wise Allocation:
                      </h6>
                      <div className="inventory-cart-channel__channel-grid">
                        {Object.entries(cart.channels || {}).map(
                          ([channelName, channel]) => {
                            const allocated = channel.allocated || 0;
                            const orders = channel.reserved || 0;
                            const available = channel.available || 0;
                            const isLowStock = available < 3 && available >= 0;
                            const isOverCommitted = available < 0;
                            const isHealthy = available >= 3;

                            return (
                              <div
                                key={channelName}
                                className="inventory-cart-channel__channel-card"
                              >
                                <div
                                  className={`${
                                    isOverCommitted
                                      ? "inventory-cart-channel__channel-card--over-committed"
                                      : isLowStock
                                      ? "inventory-cart-channel__channel-card--low-stock"
                                      : "inventory-cart-channel__channel-card--normal"
                                  }`}
                                >
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div className="inventory-cart-channel__channel-name">
                                      {channelName}
                                    </div>
                                    {isHealthy ? (
                                      <CheckCircle
                                        className="inventory-cart-channel__text--success"
                                        size={16}
                                      />
                                    ) : (
                                      <AlertTriangle
                                        className={
                                          isOverCommitted
                                            ? "inventory-cart-channel__text--danger"
                                            : "inventory-cart-channel__text--warning"
                                        }
                                        size={16}
                                      />
                                    )}
                                  </div>

                                  <div className="inventory-cart-channel__channel-metrics">
                                    <div className="inventory-cart-channel__channel-metric">
                                      <span className="inventory-cart-channel__channel-metric-label">
                                        Allocated:
                                      </span>
                                      <span className="inventory-cart-channel__channel-metric-value inventory-cart-channel__text--primary">
                                        {allocated}
                                      </span>
                                    </div>
                                    <div className="inventory-cart-channel__channel-metric">
                                      <span className="inventory-cart-channel__channel-metric-label">
                                        Orders:
                                      </span>
                                      <span className="inventory-cart-channel__channel-metric-value inventory-cart-channel__text--warning">
                                        {orders}
                                      </span>
                                    </div>
                                    <div className="inventory-cart-channel__channel-metric">
                                      <span className="inventory-cart-channel__channel-metric-label">
                                        Available:
                                      </span>
                                      <span
                                        className={`inventory-cart-channel__channel-metric-value ${
                                          isOverCommitted
                                            ? "inventory-cart-channel__text--danger"
                                            : isLowStock
                                            ? "inventory-cart-channel__text--warning"
                                            : "inventory-cart-channel__text--success"
                                        }`}
                                      >
                                        {available}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
};